import React, { useState, useEffect } from 'react';
import { canContactProperty } from '../domain/inquiry/contactPolicy';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { propertyApi, Property } from '../services/api';
import { getPropertyUrl } from '../utils/formatters';
import { resolveContactPhone } from '../domain/inquiry/resolveContactPhone';
import { getPriceBasis, priceBasisLabel } from '../domain/property/detailV2';
import { buildImageUrl } from './property-detail/helpers';
import { buildLeadPayload, generateEventId, propertyCategory, setTrackingProperty, trackLead } from '../utils/tracking';
import {
  PrivacyConsent,
  PropertySummaryCard,
  CallKakaoButtons,
  InquiryGuideCard,
  InquiryDone,
  fieldLabelClass,
  inputClass,
} from './inquiry/InquiryParts';
import './SideInquiryPage.css';

const SideInquiryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    name: '',
    phone1: '',
    phone2: '',
    propertyName: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // [필수] 개인정보 수집·이용 동의 (v2 목업 06, 2026-09-22 v2h) — 클라이언트 확인만
  const [agreed, setAgreed] = useState(false);

  // 상태별 SEO 메타 (제출 완료 / 폼 / 에러)
  useSeoMeta({
    title: submitSuccess
      ? '문의 완료 | 부냥'
      : property
        ? `사이드 문의 - ${property.title} | 부냥`
        : '사이드 문의 | 부냥',
    description: submitSuccess
      ? undefined
      : property
        ? `${property.title} 사이드 문의 - 분양 상담사 연결 서비스`
        : undefined,
    robots: submitSuccess ? undefined : 'noindex, nofollow',
  });

  // 현장 정보 로드
  useEffect(() => {
    const loadProperty = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await propertyApi.getDetail(id);
        setProperty(data);
        setFormData(prev => ({
          ...prev,
          propertyName: data.title,
        }));
      } catch (err) {
        console.error('현장 로드 실패:', err);
        setError('현장 정보를 불러올 수 없습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [id]);

  // 이 화면의 전화·카톡 클릭 Contact 이벤트 대상 현장
  useEffect(() => {
    if (!property) return undefined;
    setTrackingProperty({ id: property.id, name: property.title, category: propertyCategory(property.property_type) });
    return () => setTrackingProperty(null);
  }, [property]);

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setSubmitError('이름을 입력해주세요.');
      return;
    }

    if (!formData.phone1.trim() || !formData.phone2.trim()) {
      setSubmitError('연락처를 입력해주세요.');
      return;
    }

    if (!formData.propertyName.trim()) {
      setSubmitError('현장 이름을 입력해주세요.');
      return;
    }

    if (!agreed) {
      setSubmitError('개인정보 수집·이용에 동의해주세요.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const phone = `010-${formData.phone1}-${formData.phone2}`;
      // 광고 추적 필드(event_id·_fbp/_fbc·URL·첫 진입 utm) — 서버 CAPI Lead 와 같은 event_id
      const eventId = generateEventId();

      await propertyApi.contact(Number(id), {
        customer_name: formData.name,
        customer_phone: phone,
        property_name: formData.propertyName,
        ...buildLeadPayload(eventId),
      });

      // 저장 성공(2xx) 뒤에만 Lead. content_name 은 입력칸이 아닌 현장 제목(서버 CAPI 와 동일)
      trackLead(
        property
          ? { id: property.id, name: property.title, category: propertyCategory(property.property_type) }
          : { id: Number(id), name: formData.propertyName },
        eventId,
      );
      setSubmitSuccess(true);
    } catch (err) {
      console.error('사이드 문의 실패:', err);
      setSubmitError('문의 접수에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  // 입력 변경 핸들러
  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field.startsWith('phone')
      ? e.target.value.replace(/[^0-9]/g, '')
      : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper px-4 py-8">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-3" aria-busy="true">
          <div className="h-7 w-40 animate-pulse rounded-ca-md bg-block" />
          <div className="h-24 animate-pulse rounded-ca-3xl bg-block" />
          <div className="h-72 animate-pulse rounded-ca-3xl bg-block" />
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4">
        <div className="flex max-w-[360px] flex-col items-center gap-2 rounded-ca-3xl bg-frame p-8 text-center shadow-ca-card">
          <h2 className="text-[18px] font-bold text-ink">현장을 찾을 수 없어요</h2>
          <p className="text-[13.5px] text-muted">{error || '현장을 찾을 수 없습니다.'}</p>
          <Link to="/" className="mt-2 rounded-ca-xl bg-accent px-5 py-2.5 text-[14px] font-bold text-white">
            홈으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  // v2 목업 06 — 담당자 없는 현장(is_side_inquiry)은 전화 버튼 숨김(상세 하단 CTA 규칙과 동일)
  const phone = canContactProperty(property) ? resolveContactPhone(property) : null;
  const summary = {
    title: property.title,
    address: property.address,
    image: buildImageUrl(property.image_thumbnail_url || property.image_card_url || property.image_url),
    bannerText: property.banner_text ?? null,
    minPrice: property.min_price,
    maxPrice: property.max_price,
    priceLabel: priceBasisLabel(getPriceBasis(property)),
    moveInDate: (property as any).move_in_date ?? null,
  };

  // 제출 성공 — S6 상담 접수 완료
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-paper px-4 pb-24 pt-2">
        <div className="mx-auto max-w-[480px]">
          <InquiryDone
            title="문의가 접수되었습니다"
            customerPhone={`010-${formData.phone1}-${formData.phone2}`}
            propertyTitle={formData.propertyName || property.title}
            phone={phone}
            backTo={getPropertyUrl(property)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper pb-24">
      <div className="mx-auto flex max-w-[1200px] flex-col gap-4 px-4 pt-4 lg:flex-row lg:items-start lg:gap-6 lg:px-10 lg:pt-8">
        {/* 좌: 제목 + 폼 */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Link to={getPropertyUrl(property)} className="self-start text-[13px] font-semibold text-muted">
              ← 현장으로 돌아가기
            </Link>
            <h1 className="text-[22px] font-bold tracking-tight text-ink lg:text-[26px]">상담 문의</h1>
            <span className="text-[13.5px] text-muted">남겨주시면 담당 상담사가 배정되는 대로 연락드립니다.</span>
          </div>

          {/* 모바일: 현장 요약 카드 (PC 는 우측 사이드) */}
          <div className="lg:hidden">
            <PropertySummaryCard summary={summary} />
          </div>

          <form
            method="post"
            className="flex flex-col gap-4 rounded-ca-3xl bg-frame p-4 shadow-ca-card lg:p-6"
            onSubmit={handleSubmit}
          >
            {submitError && (
              <div className="rounded-ca-lg bg-[#FEF2F2] px-3 py-2.5 text-[13px] font-semibold text-bad" role="alert">
                {submitError}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label htmlFor="name" className={fieldLabelClass}>
                  이름 <span className="text-accent">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="홍길동"
                  value={formData.name}
                  onChange={handleChange('name')}
                  disabled={submitting}
                  required
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="phone1" className={fieldLabelClass}>
                  연락처 <span className="text-accent">*</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <span className="flex h-[46px] items-center rounded-ca-xl border border-ca-line bg-block px-3 text-[14.5px] font-bold text-ink">010</span>
                  <span className="text-muted">-</span>
                  <input
                    type="tel"
                    id="phone1"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    placeholder="0000"
                    value={formData.phone1}
                    onChange={handleChange('phone1')}
                    disabled={submitting}
                    required
                    className={`${inputClass} min-w-0 text-center`}
                  />
                  <span className="text-muted">-</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    placeholder="0000"
                    aria-label="연락처 뒷자리"
                    value={formData.phone2}
                    onChange={handleChange('phone2')}
                    disabled={submitting}
                    required
                    className={`${inputClass} min-w-0 text-center`}
                  />
                </div>
              </div>

              <div className="lg:col-span-2">
                <label htmlFor="propertyName" className={fieldLabelClass}>
                  관심 단지 <span className="text-accent">*</span>
                </label>
                <input
                  type="text"
                  id="propertyName"
                  placeholder="분양 현장 이름"
                  value={formData.propertyName}
                  onChange={handleChange('propertyName')}
                  disabled={submitting}
                  required
                  className={inputClass}
                />
              </div>
            </div>

            <PrivacyConsent checked={agreed} onChange={setAgreed} disabled={submitting} />

            <button
              type="submit"
              disabled={submitting}
              className="h-[52px] w-full rounded-ca-xl bg-accent text-[16px] font-bold text-white shadow-ca-cta transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? '전송 중...' : '상담 신청하기'}
            </button>

            <div className="lg:hidden">
              <CallKakaoButtons phone={phone} />
            </div>
          </form>
        </div>

        {/* 우: PC 현장 요약 사이드 (목업 PC 06) */}
        <aside className="hidden w-[340px] shrink-0 flex-col gap-4 lg:flex">
          <PropertySummaryCard summary={summary} phone={phone} showDetails showActions />
          <InquiryGuideCard />
        </aside>
      </div>
    </div>
  );
};

export default SideInquiryPage;
